import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Group,
  Button,
  Text,
  TextInput,
  NumberInput,
  Modal,
  Stack,
  Grid,
  Card,
  Badge,
  ActionIcon,
  Table,
  Alert,
  LoadingOverlay,
  Box,
  Divider,
  Select,
  Tabs
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconCash,
  IconMinus,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconTrash
} from '@tabler/icons-react';
import { collection, addDoc, updateDoc, doc, getDocs, query, orderBy, onSnapshot, where, writeBatch } from 'firebase/firestore';
import { db } from '../services/firebase';

const DeudasClub = () => {
  const [deudas, setDeudas] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeuda, setSelectedDeuda] = useState(null);
  
  // Modales
  const [modalNuevaDeuda, { open: openNuevaDeuda, close: closeNuevaDeuda }] = useDisclosure(false);
  const [modalPago, { open: openPago, close: closePago }] = useDisclosure(false);
  const [modalAgregarDeuda, { open: openAgregarDeuda, close: closeAgregarDeuda }] = useDisclosure(false);
  const [modalEliminarDeuda, { open: openEliminarDeuda, close: closeEliminarDeuda }] = useDisclosure(false);

  // Formularios
  const [formNuevaDeuda, setFormNuevaDeuda] = useState({
    nombre: '',
    montoTotal: 0
  });
  const [formMovimiento, setFormMovimiento] = useState({
    monto: 0,
    descripcion: ''
  });

  // Cargar datos en tiempo real
  useEffect(() => {
    setLoading(true);
    
    // Listener para deudas
    const unsubscribeDeudas = onSnapshot(
      query(collection(db, 'deudas-club'), orderBy('fechaCreacion', 'desc')),
      (snapshot) => {
        const deudasData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDeudas(deudasData);
      },
      (error) => {
        console.error('Error al cargar deudas:', error);
        notifications.show({
          title: 'Error',
          message: 'No se pudieron cargar las deudas',
          color: 'red',
          icon: <IconX />
        });
      }
    );

    // Listener para movimientos
    const unsubscribeMovimientos = onSnapshot(
      query(collection(db, 'movimientos-deudas'), orderBy('fecha', 'desc')),
      (snapshot) => {
        const movimientosData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMovimientos(movimientosData);
        setLoading(false);
      },
      (error) => {
        console.error('Error al cargar movimientos:', error);
        setLoading(false);
      }
    );

    return () => {
      unsubscribeDeudas();
      unsubscribeMovimientos();
    };
  }, []);

  const handleNuevaDeuda = async () => {
    try {
      if (!formNuevaDeuda.nombre.trim() || formNuevaDeuda.montoTotal <= 0) {
        notifications.show({
          title: 'Error',
          message: 'Por favor complete todos los campos correctamente',
          color: 'red',
          icon: <IconX />
        });
        return;
      }

      const nuevaDeuda = {
        nombre: formNuevaDeuda.nombre.trim(),
        montoTotal: formNuevaDeuda.montoTotal,
        montoPagado: 0,
        montoRestante: formNuevaDeuda.montoTotal,
        fechaCreacion: new Date(),
        activa: true
      };

      await addDoc(collection(db, 'deudas-club'), nuevaDeuda);

      notifications.show({
        title: 'Éxito',
        message: 'Deuda creada correctamente',
        color: 'green',
        icon: <IconCheck />
      });

      setFormNuevaDeuda({ nombre: '', montoTotal: 0 });
      closeNuevaDeuda();
    } catch (error) {
      console.error('Error al crear deuda:', error);
      notifications.show({
        title: 'Error',
        message: 'No se pudo crear la deuda',
        color: 'red',
        icon: <IconX />
      });
    }
  };

  const handleMovimiento = async (tipo) => {
    try {
      if (!selectedDeuda || formMovimiento.monto <= 0) {
        notifications.show({
          title: 'Error',
          message: 'Por favor ingrese un monto válido',
          color: 'red',
          icon: <IconX />
        });
        return;
      }

      const deudaRef = doc(db, 'deudas-club', selectedDeuda.id);
      let nuevoMontoPagado = selectedDeuda.montoPagado;
      let nuevoMontoTotal = selectedDeuda.montoTotal;

      if (tipo === 'pago') {
        nuevoMontoPagado += formMovimiento.monto;
        if (nuevoMontoPagado > selectedDeuda.montoTotal) {
          notifications.show({
            title: 'Error',
            message: 'El pago no puede ser mayor al monto restante',
            color: 'red',
            icon: <IconX />
          });
          return;
        }
      } else if (tipo === 'deuda') {
        nuevoMontoTotal += formMovimiento.monto;
      }

      const nuevoMontoRestante = nuevoMontoTotal - nuevoMontoPagado;

      // Actualizar la deuda
      await updateDoc(deudaRef, {
        montoTotal: nuevoMontoTotal,
        montoPagado: nuevoMontoPagado,
        montoRestante: nuevoMontoRestante
      });

      // Registrar el movimiento
      const movimiento = {
        deudaId: selectedDeuda.id,
        deudaNombre: selectedDeuda.nombre,
        tipo: tipo,
        monto: formMovimiento.monto,
        descripcion: formMovimiento.descripcion || '',
        fecha: new Date(),
        montoTotalAnterior: tipo === 'pago' ? selectedDeuda.montoTotal : selectedDeuda.montoTotal - formMovimiento.monto,
        montoPagadoAnterior: tipo === 'pago' ? selectedDeuda.montoPagado - formMovimiento.monto : selectedDeuda.montoPagado,
        montoTotalNuevo: nuevoMontoTotal,
        montoPagadoNuevo: nuevoMontoPagado
      };

      await addDoc(collection(db, 'movimientos-deudas'), movimiento);

      notifications.show({
        title: 'Éxito',
        message: `${tipo === 'pago' ? 'Pago' : 'Deuda'} registrado correctamente`,
        color: 'green',
        icon: <IconCheck />
      });

      setFormMovimiento({ monto: 0, descripcion: '' });
      setSelectedDeuda(null);
      closePago();
      closeAgregarDeuda();
    } catch (error) {
      console.error('Error al procesar movimiento:', error);
      notifications.show({
        title: 'Error',
        message: 'No se pudo procesar el movimiento',
        color: 'red',
        icon: <IconX />
      });
    }
  };

  const handleEliminarDeuda = async () => {
    try {
      if (!selectedDeuda) return;

      const batch = writeBatch(db);
      const deudaRef = doc(db, 'deudas-club', selectedDeuda.id);
      const movimientosSnapshot = await getDocs(
        query(collection(db, 'movimientos-deudas'), where('deudaId', '==', selectedDeuda.id))
      );

      movimientosSnapshot.forEach((movimientoDoc) => {
        batch.delete(movimientoDoc.ref);
      });
      batch.delete(deudaRef);

      await batch.commit();

      notifications.show({
        title: 'Éxito',
        message: 'Deuda eliminada correctamente',
        color: 'green',
        icon: <IconCheck />
      });

      setSelectedDeuda(null);
      closeEliminarDeuda();
    } catch (error) {
      console.error('Error al eliminar deuda:', error);
      notifications.show({
        title: 'Error',
        message: 'No se pudo eliminar la deuda',
        color: 'red',
        icon: <IconX />
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalFaltante = deudas.reduce((sum, deuda) => sum + (deuda.montoRestante || 0), 0);
  const totalPagado = deudas.reduce((sum, deuda) => sum + (deuda.montoPagado || 0), 0);
  const deudasPendientes = deudas.filter((deuda) => (deuda.montoRestante || 0) > 0);
  const deudasTerminadas = deudas.filter((deuda) => (deuda.montoRestante || 0) === 0);

  const renderDeudasGrid = (items, emptyTitle, emptyMessage) => {
    if (items.length === 0) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={emptyTitle}
          color="blue"
        >
          {emptyMessage}
        </Alert>
      );
    }

    return (
      <Grid>
        {items.map((deuda) => {
          const porcentajePagado = deuda.montoTotal > 0 ? (deuda.montoPagado / deuda.montoTotal) * 100 : 0;
          return (
            <Grid.Col key={deuda.id} span={{ base: 12, md: 6, lg: 4 }}>
              <Card withBorder h="100%">
                <Stack spacing="sm">
                  <Group justify="space-between" align="flex-start">
                    <Text fw={600} size="lg">{deuda.nombre}</Text>
                    <Badge
                      color={deuda.montoRestante === 0 ? 'green' : 'red'}
                      variant="light"
                    >
                      {deuda.montoRestante === 0 ? 'Pagada' : 'Pendiente'}
                    </Badge>
                  </Group>

                  <div>
                    <Text size="sm" c="dimmed">Total: {formatCurrency(deuda.montoTotal)}</Text>
                    <Text size="sm" c="green">Pagado: {formatCurrency(deuda.montoPagado)}</Text>
                    <Text size="sm" c="red" fw={600}>Restante: {formatCurrency(deuda.montoRestante)}</Text>
                    <Text size="xs" c="dimmed">Progreso: {porcentajePagado.toFixed(1)}%</Text>
                  </div>

                  <Group mt="md">
                    <Button
                      size="xs"
                      leftSection={<IconCash size={14} />}
                      color="green"
                      variant="light"
                      disabled={deuda.montoRestante === 0}
                      onClick={() => {
                        setSelectedDeuda(deuda);
                        openPago();
                      }}
                    >
                      Pagar
                    </Button>
                    <Button
                      size="xs"
                      leftSection={<IconPlus size={14} />}
                      color="red"
                      variant="light"
                      onClick={() => {
                        setSelectedDeuda(deuda);
                        openAgregarDeuda();
                      }}
                    >
                      + Deuda
                    </Button>
                    <Button
                      size="xs"
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      variant="subtle"
                      onClick={() => {
                        setSelectedDeuda(deuda);
                        openEliminarDeuda();
                      }}
                    >
                      Borrar
                    </Button>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>
          );
        })}
      </Grid>
    );
  };

  return (
    <Container size="xl">
      <LoadingOverlay visible={loading} />
      
      <Stack spacing="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={1} mb="xs">Deudas Club</Title>
            <Text size="lg" c="dimmed">
              Gestión de deudas del club - Solo administradores
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={openNuevaDeuda}
            variant="gradient"
            gradient={{ from: 'blue', to: 'cyan' }}
          >
            Nueva Deuda
          </Button>
        </Group>

        {/* Resumen General */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Card withBorder>
              <Text size="sm" c="dimmed" fw={500}>Total Faltante</Text>
              <Text size="xl" fw={700} c="red">{formatCurrency(totalFaltante)}</Text>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Card withBorder>
              <Text size="sm" c="dimmed" fw={500}>Total Pagado</Text>
              <Text size="xl" fw={700} c="green">{formatCurrency(totalPagado)}</Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Lista de Deudas */}
        <Paper p="md" withBorder>
          <Title order={3} mb="md">Estado de Deudas</Title>
          <Tabs defaultValue="pendientes" keepMounted={false}>
            <Tabs.List mb="md">
              <Tabs.Tab value="pendientes">
                Pendientes ({deudasPendientes.length})
              </Tabs.Tab>
              <Tabs.Tab value="terminadas">
                Deudas terminadas ({deudasTerminadas.length})
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="pendientes">
              {renderDeudasGrid(
                deudasPendientes,
                'No hay deudas pendientes',
                deudas.length === 0
                  ? 'Comience agregando una nueva deuda utilizando el botón "Nueva Deuda".'
                  : 'Todas las deudas están pagadas.'
              )}
            </Tabs.Panel>

            <Tabs.Panel value="terminadas">
              {renderDeudasGrid(
                deudasTerminadas,
                'No hay deudas terminadas',
                'Las deudas pagadas aparecerán en esta pestaña.'
              )}
            </Tabs.Panel>
          </Tabs>
        </Paper>

        {/* Historial de Movimientos */}
        <Paper p="md" withBorder>
          <Title order={3} mb="md">Historial de Movimientos</Title>
          {movimientos.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No hay movimientos registrados
            </Text>
          ) : (
            <Box style={{ overflowX: 'auto' }}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Fecha</Table.Th>
                    <Table.Th>Deuda</Table.Th>
                    <Table.Th>Tipo</Table.Th>
                    <Table.Th>Monto</Table.Th>
                    <Table.Th>Descripción</Table.Th>
                    <Table.Th>Saldo Anterior</Table.Th>
                    <Table.Th>Saldo Nuevo</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {movimientos.map((movimiento) => (
                    <Table.Tr key={movimiento.id}>
                      <Table.Td>{formatDate(movimiento.fecha)}</Table.Td>
                      <Table.Td>{movimiento.deudaNombre}</Table.Td>
                      <Table.Td>
                        <Badge
                          color={movimiento.tipo === 'pago' ? 'green' : 'red'}
                          variant="light"
                        >
                          {movimiento.tipo === 'pago' ? 'Pago' : 'Deuda'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text c={movimiento.tipo === 'pago' ? 'green' : 'red'} fw={600}>
                          {movimiento.tipo === 'pago' ? '-' : '+'}{formatCurrency(movimiento.monto)}
                        </Text>
                      </Table.Td>
                      <Table.Td>{movimiento.descripcion || '-'}</Table.Td>
                      <Table.Td>
                        {formatCurrency(
                          (movimiento.montoTotalAnterior || 0) - (movimiento.montoPagadoAnterior || 0)
                        )}
                      </Table.Td>
                      <Table.Td>
                        {formatCurrency(
                          (movimiento.montoTotalNuevo || 0) - (movimiento.montoPagadoNuevo || 0)
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          )}
        </Paper>
      </Stack>

      {/* Modal Nueva Deuda */}
      <Modal
        opened={modalNuevaDeuda}
        onClose={closeNuevaDeuda}
        title="Nueva Deuda"
        size="md"
      >
        <Stack>
          <TextInput
            label="Nombre de la deuda"
            placeholder="Ej: Préstamo banco, Deuda proveedor..."
            value={formNuevaDeuda.nombre}
            onChange={(e) => setFormNuevaDeuda({ ...formNuevaDeuda, nombre: e.target.value })}
            required
          />
          <NumberInput
            label="Monto total"
            placeholder="0"
            value={formNuevaDeuda.montoTotal}
            onChange={(value) => setFormNuevaDeuda({ ...formNuevaDeuda, montoTotal: value || 0 })}
            min={0}
            hideControls
            thousandSeparator=","
            decimalSeparator="."
            prefix="$"
            required
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeNuevaDeuda}>
              Cancelar
            </Button>
            <Button onClick={handleNuevaDeuda}>
              Crear Deuda
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal Agregar Pago */}
      <Modal
        opened={modalPago}
        onClose={closePago}
        title={`Agregar Pago - ${selectedDeuda?.nombre}`}
        size="md"
      >
        <Stack>
          <Alert color="blue" variant="light">
            <Text size="sm">
              <strong>Deuda:</strong> {formatCurrency(selectedDeuda?.montoRestante || 0)}
            </Text>
          </Alert>
          <NumberInput
            label="Monto del pago"
            placeholder="0"
            value={formMovimiento.monto}
            onChange={(value) => setFormMovimiento({ ...formMovimiento, monto: value || 0 })}
            min={0}
            max={selectedDeuda?.montoRestante || 0}
            hideControls
            thousandSeparator=","
            decimalSeparator="."
            prefix="$"
            required
          />
          <TextInput
            label="Descripción (opcional)"
            placeholder="Detalle del pago..."
            value={formMovimiento.descripcion}
            onChange={(e) => setFormMovimiento({ ...formMovimiento, descripcion: e.target.value })}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closePago}>
              Cancelar
            </Button>
            <Button color="green" onClick={() => handleMovimiento('pago')}>
              Registrar Pago
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal Agregar Deuda */}
      <Modal
        opened={modalAgregarDeuda}
        onClose={closeAgregarDeuda}
        title={`Agregar Más Deuda - ${selectedDeuda?.nombre}`}
        size="md"
      >
        <Stack>
          <Alert color="orange" variant="light">
            <Text size="sm">
              <strong>Total actual:</strong> {formatCurrency(selectedDeuda?.montoTotal || 0)}
            </Text>
          </Alert>
          <NumberInput
            label="Monto adicional"
            placeholder="0"
            value={formMovimiento.monto}
            onChange={(value) => setFormMovimiento({ ...formMovimiento, monto: value || 0 })}
            min={0}
            hideControls
            thousandSeparator=","
            decimalSeparator="."
            prefix="$"
            required
          />
          <TextInput
            label="Descripción (opcional)"
            placeholder="Motivo del aumento..."
            value={formMovimiento.descripcion}
            onChange={(e) => setFormMovimiento({ ...formMovimiento, descripcion: e.target.value })}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeAgregarDeuda}>
              Cancelar
            </Button>
            <Button color="red" onClick={() => handleMovimiento('deuda')}>
              Agregar Deuda
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal Eliminar Deuda */}
      <Modal
        opened={modalEliminarDeuda}
        onClose={closeEliminarDeuda}
        title="Eliminar deuda"
        size="md"
      >
        <Stack>
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              Se eliminará <strong>{selectedDeuda?.nombre}</strong> y todo su historial de movimientos.
            </Text>
          </Alert>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeEliminarDeuda}>
              Cancelar
            </Button>
            <Button color="red" leftSection={<IconTrash size={16} />} onClick={handleEliminarDeuda}>
              Eliminar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default DeudasClub;
